import { Suspense, lazy } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { AdminLayout } from '@/components/admin/AdminLayout';
import { AppLayout } from '@/components/layout/AppLayout';
import { Button } from '@/components/ui/Button';
import { EmptyState, LoadingScreen } from '@/components/ui/misc';
import { LoginPage } from '@/pages/auth/LoginPage';
import { LandingPage } from '@/pages/landing/LandingPage';
import { DashboardPage } from '@/pages/DashboardPage';
import { useAuth } from '@/providers/AuthProvider';

/**
 * Страницы грузятся по требованию.
 *
 * Одним куском сборка весила 1,4 МБ: каждый входящий скачивал и разбирал
 * админ-панель, отчёты и склад документов, даже если никогда их не откроет.
 * На телефоне в дороге это разница между «открылось» и «крутится».
 *
 * В основном куске остаются только вход, лендинг и дашборд — то, что человек
 * видит в первые секунды. Всё остальное подгружается при переходе.
 */
const AdminAuditPage = lazy(() => import('@/pages/admin/AdminAuditPage').then((m) => ({ default: m.AdminAuditPage })));
const AdminErrorsPage = lazy(() => import('@/pages/admin/AdminErrorsPage').then((m) => ({ default: m.AdminErrorsPage })));
const AdminInvitesPage = lazy(() => import('@/pages/admin/AdminInvitesPage').then((m) => ({ default: m.AdminInvitesPage })));
const AdminLoginPage = lazy(() => import('@/pages/admin/AdminLoginPage').then((m) => ({ default: m.AdminLoginPage })));
const AdminMaintenancePage = lazy(() => import('@/pages/admin/AdminMaintenancePage').then((m) => ({ default: m.AdminMaintenancePage })));
const AdminOverviewPage = lazy(() => import('@/pages/admin/AdminOverviewPage').then((m) => ({ default: m.AdminOverviewPage })));
const AdminPlansPage = lazy(() => import('@/pages/admin/AdminPlansPage').then((m) => ({ default: m.AdminPlansPage })));
const AdminSettingsPage = lazy(() => import('@/pages/admin/AdminSettingsPage').then((m) => ({ default: m.AdminSettingsPage })));
const AdminSupportPage = lazy(() => import('@/pages/admin/AdminSupportPage').then((m) => ({ default: m.AdminSupportPage })));
const AdminUsersPage = lazy(() => import('@/pages/admin/AdminUsersPage').then((m) => ({ default: m.AdminUsersPage })));
const AdminWorkspacesPage = lazy(() => import('@/pages/admin/AdminWorkspacesPage').then((m) => ({ default: m.AdminWorkspacesPage })));
const CalendarPage = lazy(() => import('@/pages/finance/CalendarPage').then((m) => ({ default: m.CalendarPage })));
const CreateWorkspacePage = lazy(() => import('@/pages/onboarding/CreateWorkspacePage').then((m) => ({ default: m.CreateWorkspacePage })));
const DevLogPage = lazy(() => import('@/pages/devlog/DevLogPage').then((m) => ({ default: m.DevLogPage })));
const DocumentsPage = lazy(() => import('@/pages/documents/DocumentsPage').then((m) => ({ default: m.DocumentsPage })));
const IncomePage = lazy(() => import('@/pages/finance/IncomePage').then((m) => ({ default: m.IncomePage })));
const NotificationsPage = lazy(() => import('@/pages/NotificationsPage').then((m) => ({ default: m.NotificationsPage })));
const PayoutsPage = lazy(() => import('@/pages/payouts/PayoutsPage').then((m) => ({ default: m.PayoutsPage })));
const PeoplePage = lazy(() => import('@/pages/people/PeoplePage').then((m) => ({ default: m.PeoplePage })));
const PersonPage = lazy(() => import('@/pages/people/PersonPage').then((m) => ({ default: m.PersonPage })));
const PortalPage = lazy(() => import('@/pages/portal/PortalPage').then((m) => ({ default: m.PortalPage })));
const RecoverPage = lazy(() => import('@/pages/auth/RecoverPage').then((m) => ({ default: m.RecoverPage })));
const RecurringPage = lazy(() => import('@/pages/finance/RecurringPage').then((m) => ({ default: m.RecurringPage })));
const RegisterPage = lazy(() => import('@/pages/auth/RegisterPage').then((m) => ({ default: m.RegisterPage })));
const ReportsPage = lazy(() => import('@/pages/reports/ReportsPage').then((m) => ({ default: m.ReportsPage })));
const SettingsPage = lazy(() => import('@/pages/settings/SettingsPage').then((m) => ({ default: m.SettingsPage })));
const StartPage = lazy(() => import('@/pages/StartPage').then((m) => ({ default: m.StartPage })));
const SupportPage = lazy(() => import('@/pages/support/SupportPage').then((m) => ({ default: m.SupportPage })));
const TransactionsPage = lazy(() => import('@/pages/finance/TransactionsPage').then((m) => ({ default: m.TransactionsPage })));
const WalletsPage = lazy(() => import('@/pages/finance/WalletsPage').then((m) => ({ default: m.WalletsPage })));

/**
 * Маршруты и защита доступа.
 *
 * Три состояния сессии: пока идёт восстановление по cookie — экран загрузки;
 * без сессии — только страницы входа; с сессией, но без воркспейса —
 * мастер создания.
 */
export function AppRoutes() {
  const { status } = useAuth();

  if (status === 'loading') {
    return <LoadingScreen />;
  }

  return (
    // Пока подгружается кусок страницы, показываем тот же экран загрузки,
    // что и при восстановлении сессии: мигание пустотой хуже ожидания.
    <Suspense fallback={<LoadingScreen />}>
        <Routes>
        <Route path="/login" element={<AnonymousOnly><LoginPage /></AnonymousOnly>} />
        <Route path="/register" element={<AnonymousOnly><RegisterPage /></AnonymousOnly>} />
        <Route path="/recover" element={<AnonymousOnly><RecoverPage /></AnonymousOnly>} />

        <Route path="/workspace/new" element={<Protected requireWorkspace={false}><CreateWorkspacePage /></Protected>} />

        {/* Корень раздваивается: вошедшему — дашборд, гостю — лендинг.
          Отдельный адрес вроде /about никто бы не открыл, а человеку по ссылке
          нужно сначала понять, куда он попал. */}
        <Route path="/" element={status === 'authenticated' ? <Protected><AppLayout><DashboardPage /></AppLayout></Protected> : <LandingPage />} />
        <Route path="/notifications" element={<Protected><AppLayout><NotificationsPage /></AppLayout></Protected>} />
        <Route path="/start" element={<Protected><AppLayout><StartPage /></AppLayout></Protected>} />
        <Route path="/people" element={<Protected><AppLayout><PeoplePage /></AppLayout></Protected>} />
        <Route path="/people/:id" element={<Protected><AppLayout><PersonPage /></AppLayout></Protected>} />
        <Route path="/payouts" element={<Protected><AppLayout><PayoutsPage /></AppLayout></Protected>} />
        <Route path="/wallets" element={<Protected><AppLayout><WalletsPage /></AppLayout></Protected>} />
        <Route path="/calendar" element={<Protected><AppLayout><CalendarPage /></AppLayout></Protected>} />
        <Route path="/income" element={<Protected><AppLayout><IncomePage /></AppLayout></Protected>} />
        <Route path="/recurring" element={<Protected><AppLayout><RecurringPage /></AppLayout></Protected>} />
        <Route path="/transactions" element={<Protected><AppLayout><TransactionsPage /></AppLayout></Protected>} />
        <Route path="/support" element={<Protected requireWorkspace={false}><AppLayout><SupportPage /></AppLayout></Protected>} />

        {/* Вкладка в адресе: ссылку на «Участников» можно кинуть коллеге. */}
        {/* Личный кабинет игрока: открывается по ссылке, вход не нужен. */}
        <Route path="/portal/:token" element={<PortalPage />} />

        <Route path="/devlog" element={<Protected requireWorkspace={false}><AppLayout><DevLogPage /></AppLayout></Protected>} />
        <Route path="/documents" element={<Protected><AppLayout><DocumentsPage /></AppLayout></Protected>} />
        <Route path="/reports" element={<Protected><AppLayout><ReportsPage /></AppLayout></Protected>} />
        <Route path="/settings" element={<Protected><AppLayout><SettingsPage /></AppLayout></Protected>} />
        <Route path="/settings/:tab" element={<Protected><AppLayout><SettingsPage /></AppLayout></Protected>} />

        {/* Админ-панель. Отдельный понятный путь входа — /admin/login. */}
        <Route path="/admin/login" element={<AdminLoginPage />} />
        <Route path="/admin" element={<AdminOnly><AdminOverviewPage /></AdminOnly>} />
        <Route path="/admin/users" element={<AdminOnly><AdminUsersPage /></AdminOnly>} />
        <Route path="/admin/workspaces" element={<AdminOnly><AdminWorkspacesPage /></AdminOnly>} />
        <Route path="/admin/plans" element={<AdminOnly><AdminPlansPage /></AdminOnly>} />
        <Route path="/admin/invites" element={<AdminOnly><AdminInvitesPage /></AdminOnly>} />
        <Route path="/admin/support" element={<AdminOnly><AdminSupportPage /></AdminOnly>} />
        <Route path="/admin/audit" element={<AdminOnly><AdminAuditPage /></AdminOnly>} />
        <Route path="/admin/errors" element={<AdminOnly><AdminErrorsPage /></AdminOnly>} />
        <Route path="/admin/settings" element={<AdminOnly><AdminSettingsPage /></AdminOnly>} />
        <Route path="/admin/maintenance" element={<AdminOnly><AdminMaintenancePage /></AdminOnly>} />

        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}

/**
 * Доступ в админ-панель.
 *
 * Настоящая проверка — на сервере: каждый запрос `/api/admin/*` смотрит роль
 * в базе. Здесь только маршрутизация, чтобы не показывать пустые экраны с
 * ошибками тому, кому раздел не предназначен.
 */
function AdminOnly({ children }: { children: React.ReactNode }) {
  const { status, me } = useAuth();

  if (status !== 'authenticated') return <Navigate to="/admin/login" replace />;

  // Вошёл, но не администратор — уводим в приложение, а не показываем отказ:
  // чаще всего это просто чужая ссылка, а не попытка что-то взломать.
  if (me?.user.role !== 'SUPERADMIN') return <Navigate to="/" replace />;

  return <AdminLayout>{children}</AdminLayout>;
}

/** Страницы входа не должны показываться уже вошедшему пользователю. */
function AnonymousOnly({ children }: { children: React.ReactNode }) {
  const { status } = useAuth();
  const location = useLocation();

  if (status === 'authenticated') {
    const from = (location.state as { from?: string } | null)?.from ?? '/';
    return <Navigate to={from} replace />;
  }
  return <>{children}</>;
}

function Protected({
  children,
  requireWorkspace = true,
}: {
  children: React.ReactNode;
  requireWorkspace?: boolean;
}) {
  const { status, workspaces } = useAuth();
  const location = useLocation();

  if (status !== 'authenticated') {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  // Аккаунт есть, а организации ещё нет — ведём в мастер, без него работать не с чем.
  if (requireWorkspace && workspaces.length === 0) {
    return <Navigate to="/workspace/new" replace />;
  }

  return <>{children}</>;
}

function NotFound() {
  const { t } = useTranslation(['errors', 'common']);
  const { status } = useAuth();

  const content = (
    <EmptyState
      title={t('errors:pageNotFound')}
      description={t('errors:pageNotFoundBody')}
      action={
        <Button onClick={() => window.location.assign('/')}>{t('errors:goHome')}</Button>
      }
    />
  );

  if (status === 'authenticated') return <AppLayout>{content}</AppLayout>;

  return <div className="flex min-h-screen items-center justify-center">{content}</div>;
}
